import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgForOf, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { forkJoin, Observable } from 'rxjs';
import { MessageNotificationService } from 'src/app/shared/services/message-notification.service';
import { AdminService } from '../../admin/admin.service';
import { LoadingComponent } from '../../loading/loading.component';
import { DataToolsLiteMessage } from '../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../shared/services/datatools-lite.service';
import { UtilsService } from '../../shared/services/utils.service';
import { DataToolsSettingsService } from '../data-tools-settings.service';
import { DuplikitOptionsService } from '../duplikit-options/duplikit-options.service';
import { IBullhornFieldAndLabel, IBullhornFields } from '../weight-table/weights-interfaces';
import { MasterIdentificationService } from './master-identification.service';

@Component({
  selector: 'app-master-identification-options',
  templateUrl: './master-identification-options.component.html',
  imports: [
    NgIf,
    MatTooltip,
    CdkDropList,
    CdkDrag,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatSelect,
    MatOption,
    NgForOf,
    FormsModule,
    LoadingComponent,
    CdkDragPlaceholder
  ],
  styleUrls: ['./master-identification-options.component.scss']
})
export class MasterIdentificationOptionsComponent implements OnInit {
  loaded = false;
  showTooltip = true;

  private readonly operationTypeOptions: IOperationTypeOption[] = [
    { value: OperationType.POPULATED_STATUS, label: 'Empty/Populated' },
    { value: OperationType.ITEM_COUNT, label: 'Least/Most' },
    { value: OperationType.MOST_RECENT_ASSOCIATED_ENTITY, label: 'Oldest/Newest' }
  ];

  constructor(
    private duplikitOptionsService: DuplikitOptionsService,
    private adminService: AdminService,
    private datatoolsLiteService: DatatoolsLiteService,
    private messageNotificationService: MessageNotificationService,
    private dataToolsSettingsService: DataToolsSettingsService,
    private masterIdentificationService: MasterIdentificationService,
    private utilsService: UtilsService
  ) {
  }

  ngOnInit() {

    const getAllCustomSelections$: Observable<IBullhornFields> = this.masterIdentificationService.getAvailableFields();
    const getAllCustomStrategies$: Observable<IStrategyCustomMapping> = this.masterIdentificationService.getCustomStrategy();
    const getStrategies$: Observable<IStrategySelections> = this.masterIdentificationService.getStrategy();

    const result$ = forkJoin({
      customSelections: getAllCustomSelections$,
      customStrategies: getAllCustomStrategies$,
      dataReceived: getStrategies$
    });

    result$.subscribe(({customSelections, customStrategies, dataReceived}) => {
      this.masterIdentificationService.availableMasterIdentificationFields = customSelections;
      for (const entity in this.masterIdentificationService.availableMasterIdentificationFields) {
        this.masterIdentificationService.availableMasterIdentificationFields[entity].sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel));
      }
      this.masterIdentificationService.customStrategyMapping = customStrategies;
      this.masterIdentificationService.strategySelections = dataReceived;
      this.loaded = true;
    }, (error) => {
      this.messageNotificationService.error('Failed to get master identification data. Please reload and try again.', error);
    });
  }

  getUserFriendlyLabel(field: string, entity: string): string {
    switch (field) {
      case 'date_added': return 'Date Added';
      case 'date_last_note': return 'Last Note Added';
      case 'date_last_modified': return 'Last Modified';
      case 'date_placement': return 'Placement Date Added';
      case 'bullhornID': return 'Bullhorn ID';
      default: return this.getBhFieldForMaster(field, entity)?.fieldLabel || field;
    }
  }

  getTooltip(field: string, entity: string): string {
    let tooltip = null;
    if (field === 'date_last_note') {
      let entityLabel = this.utilsService.getEntityLabel(entity);
      tooltip = 'Field maintained by Bullhorn. Uses ' + entityLabel + ' dateLastComment, not the most recent dateAdded of ' + entityLabel + '\'s associated Notes.';
    }
    return tooltip;
  }

  getBhFieldForMaster(column: string, entity: string): IBullhornFieldAndLabel {
    const customMasterField = this.getCustomStrategyMapping(entity).find(val => val.fieldIdentifier === column);
    const bhfield = this.getCustomStrategyMappingFields(entity).find(val => val.fieldName === customMasterField.bullhornField);
    return bhfield;
  }

  getAscDescLabel(cd: IColumnAndDirection, entity: string, direction: string) {

    if (cd.column.indexOf('date_') > -1 || cd.column.indexOf('bullhornID') > -1) {
      return direction === SortDirection.ASC ? 'Oldest' : 'Newest';
    } else if (cd.column.indexOf('customMaster') > -1) {
      const bhfield = this.getBhFieldForMaster(cd.column, entity);

      // For TO_MANY fields, check the operation type
      if (bhfield.type === 'TO_MANY') {
        const customMasterField: ICustomMasterPairing = this.getCustomStrategyMapping(entity).find((cm: ICustomMasterPairing) => cm.fieldIdentifier === cd.column);
        const operation: OperationType = customMasterField?.operationType;

        if (operation === OperationType.ITEM_COUNT) {
          return direction === SortDirection.ASC ? 'Least' : 'Most';
        } else if (operation === OperationType.MOST_RECENT_ASSOCIATED_ENTITY) {
          return direction === SortDirection.ASC ? 'Oldest' : 'Newest';
        } else {
          return direction === SortDirection.ASC ? 'Empty' : 'Populated';
        }
      } else if (bhfield.dataType === 'String' || bhfield.type === 'TO_ONE') {
        return direction === SortDirection.ASC ? 'Empty' : 'Populated';
      } else if (bhfield.dataType === 'Timestamp') {
        return direction === SortDirection.ASC ? 'Oldest' : 'Newest';
      } else if (bhfield.dataType === 'Integer' || bhfield.dataType === 'Double') {
        return direction === SortDirection.ASC ? 'Smallest' : 'Largest';
      } else {
        return direction === SortDirection.ASC ? 'Asc' : 'Desc';
      }
    }
  }

  getCustomStrategyMappingFields(entity: string): IBullhornFieldAndLabel[] {
    entity = (entity === 'clientCorporation') ? 'company' : entity;
    entity = (entity === 'clientContact') ? 'contact' : entity;
    return this.masterIdentificationService.availableMasterIdentificationFields[entity];
  }

  getCustomStrategyMapping(entity: string): ICustomMasterPairing[] {
    return this.masterIdentificationService.customStrategyMapping[entity];
  }

  getStrategySelections(entity: string): IColumnAndDirection[] {
    return this.masterIdentificationService.strategySelections[entity];
  }

  drop(event: CdkDragDrop<string[]>, array, entity) {
    if(event.previousIndex !== event.currentIndex) {
      this.simpleMasterIdentificationChange(entity)
    }
    moveItemInArray(array, event.previousIndex, event.currentIndex);
  }

  simpleMasterIdentificationChange(entity: 'candidate' | 'clientContact' | 'clientCorporation'): void {
    this.dataToolsSettingsService.masterIDScriptRequired.next(entity);
    this.masterIdentificationHasChanged(entity);
  }

  masterIdentificationHasChanged(entity: string): void {
    this.duplikitOptionsService.masterIdentificationHasChanged[entity].next(true);
    this.duplikitOptionsService.entityRefresh[entity] = true;
  }

  customMasterIdentificationHasChanged(entity: 'candidate' | 'clientContact' | 'clientCorporation', customMasterField: ICustomMasterPairing): void {
    this.masterIdentificationHasChanged(entity);

    if (customMasterField.fieldIdentifier && customMasterField.bullhornField) {
      const strategySelections: IColumnAndDirection[] = this.masterIdentificationService.strategySelections[entity];

      // Look up field metadata from available fields
      const availableFields: IBullhornFieldAndLabel[] = this.getCustomStrategyMappingFields(entity);
      const bhField: IBullhornFieldAndLabel = availableFields.find(f => f.fieldName === customMasterField.bullhornField);

      if (bhField) {
        // For TO_MANY fields, operation is required - default to POPULATED_STATUS if not set
        if (bhField.type === 'TO_MANY' && !customMasterField.operationType) {
          customMasterField.operationType = OperationType.POPULATED_STATUS;
        }

        // For non-TO_MANY fields, clear operation (not applicable)
        if (bhField.type !== 'TO_MANY') {
          customMasterField.operationType = undefined;
        }
      }

      this.upsertCustomMaster(strategySelections, customMasterField.fieldIdentifier);

      // Always trigger sync when custom master field changes (needs to pull new field data from Bullhorn)
      this.dataToolsSettingsService.syncRequired.next(entity);
    }
  }

  upsertCustomMaster(strategySelections: IColumnAndDirection[], customMaster: string): void {
    const indexIfExists = strategySelections.findIndex(selection =>
      selection.column === customMaster
    );
    const newCustomMaster: IColumnAndDirection = { column: customMaster as any, direction: SortDirection.DESC };
    if (indexIfExists > -1) {
      strategySelections.splice(indexIfExists, 1, newCustomMaster);
    } else {
      strategySelections.push(newCustomMaster);
    }
  }

  clearSelection(entity: string, customMaster: ICustomMasterPairing) {
    const index = this.masterIdentificationService.strategySelections[entity].findIndex( master =>
      (customMaster.fieldIdentifier === master.column) );
    this.masterIdentificationService.strategySelections[entity].splice( index, 1 );
    customMaster.bullhornField = '';
    customMaster.operationType = undefined;
    this.dataToolsSettingsService.masterIDScriptRequired.next(entity);
    this.masterIdentificationHasChanged(entity);
  }

  get isDisabled() {
    return this.adminService.isInLiteMode();
  }

  showDataToolsLiteMessage() {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_CUSTOM_MASTER);
      return;
    }
  }

  isToManyField(customMasterField: ICustomMasterPairing, entity: string): boolean {
    if (!customMasterField || !customMasterField.bullhornField) {
      return false;
    }

    const availableFields: IBullhornFieldAndLabel[] = this.getCustomStrategyMappingFields(entity);
    const fieldMetadata: IBullhornFieldAndLabel = availableFields.find(f => f.fieldName === customMasterField.bullhornField);

    return fieldMetadata ? fieldMetadata.type === 'TO_MANY' : false;
  }

  isFieldAlreadySelected(fieldName: string, entity: string, currentFieldIdentifier: string): boolean {
    return this.getCustomStrategyMapping(entity).some(field =>
      field.bullhornField === fieldName && field.fieldIdentifier !== currentFieldIdentifier
    );
  }

  getComparisonType(customMasterField: ICustomMasterPairing): OperationType {
    return customMasterField.operationType || OperationType.POPULATED_STATUS;
  }

  setComparisonType(entity: string, customMasterField: ICustomMasterPairing, type: OperationType): void {
    customMasterField.operationType = type;
    this.masterIdentificationHasChanged(entity);
    this.dataToolsSettingsService.syncRequired.next(entity);
  }

  getOperationTypeOptions(): IOperationTypeOption[] {
    return this.operationTypeOptions;
  }
}

export interface ICustomMasterPairing {
  fieldIdentifier: string;
  bullhornField: string;
  operationType?: OperationType;  // Comparison type for TO_MANY fields
}

export interface IOperationTypeOption {
  value: OperationType;
  label: string;
}

export interface IStrategySelections {
  candidate: IColumnAndDirection[];
  clientContact: IColumnAndDirection[];
  clientCorporation: IColumnAndDirection[];
}

export interface IStrategyCustomMapping {
  candidate: ICustomMasterPairing[];
  clientContact: ICustomMasterPairing[];
  clientCorporation: ICustomMasterPairing[];
}

export interface IColumnAndDirection {
  column: Column;
  direction: SortDirection;
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC'
}

export enum OperationType {
  /** Compare by whether the field is populated or empty */
  POPULATED_STATUS = 'POPULATED_STATUS',
  /** Compare by the count of associated items */
  ITEM_COUNT = 'ITEM_COUNT',
  /** Compare by the most/least recent associated entity date */
  MOST_RECENT_ASSOCIATED_ENTITY = 'MOST_RECENT_ASSOCIATED_ENTITY'
}

export enum Column {
  DateLastNote = 'date_last_note',
  DateAdded = 'date_added',
  BullhornID = 'bullhornID',
  DatePlacement = 'date_placement',
  DateLastModified = 'date_last_modified'
}
